(function () {
  'use strict';

  if (window.__tapqueAnalyticsBridgeLoaded) return;
  window.__tapqueAnalyticsBridgeLoaded = true;

  const CUSTOM_EVENT_NAME = 'tapque_anchor_clicked';
  const FUNNEL_EVENT_NAME = 'tapque_funnel_event';
  const ALLOWED_FIELDS = [
    'anchor_id',
    'anchor_category',
    'page_area',
    'placement',
    'product_id',
    'variant_id',
  ];
  const FUNNEL_FIELDS = [
    'schema_version',
    'funnel_stage',
    'product_id',
    'product_handle',
    'variant_id',
    'template_suffix',
    'page_area',
    'placement',
    'currency',
    'value',
    'quantity',
    'error_code',
  ];
  const FUNNEL_STAGES = new Set([
    'product_viewed',
    'variant_selected',
    'add_to_cart_attempted',
    'add_to_cart_succeeded',
    'add_to_cart_failed',
    'checkout_started',
  ]);
  const seenProductViews = new Set();
  let lastVariantId = null;
  let pendingAddToCart = null;
  let submitSequence = 0;

  function publish(payload) {
    if (window.Shopify && window.Shopify.designMode) return;
    if (!window.Shopify || !window.Shopify.analytics || typeof window.Shopify.analytics.publish !== 'function') return;

    const safePayload = {};
    ALLOWED_FIELDS.forEach((key) => {
      const value = payload[key];
      if (typeof value === 'string' && value.trim()) safePayload[key] = value.trim();
    });

    if (!safePayload.anchor_id) return;

    try {
      window.Shopify.analytics.publish(CUSTOM_EVENT_NAME, safePayload);
    } catch (_error) {
      // Analytics must never interrupt storefront interactions.
    }
  }

  function publishFunnel(stage, payload) {
    if (!FUNNEL_STAGES.has(stage)) return;
    if (window.Shopify && window.Shopify.designMode) return;
    if (!window.Shopify || !window.Shopify.analytics || typeof window.Shopify.analytics.publish !== 'function') return;

    const safePayload = { schema_version: '1', funnel_stage: stage };
    FUNNEL_FIELDS.forEach((key) => {
      const value = payload && payload[key];
      if (typeof value === 'number' && Number.isFinite(value)) safePayload[key] = value;
      if (typeof value === 'string' && value.trim()) safePayload[key] = value.trim();
    });

    try {
      window.Shopify.analytics.publish(FUNNEL_EVENT_NAME, safePayload);
    } catch (_error) {
      // Analytics must never interrupt storefront interactions.
    }
  }

  function closestFromEvent(event, selector) {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    for (const node of path) {
      if (node instanceof Element && node.matches(selector)) return node;
    }
    return event.target instanceof Element ? event.target.closest(selector) : null;
  }

  function pageArea(element) {
    if (element.closest('header, .shopify-section-header')) return 'header';
    if (element.closest('cart-drawer, #CartDrawer')) return 'cart_drawer';
    if (element.closest('footer, .shopify-section-footer')) return 'footer';
    if (element.closest('.card-wrapper, .card, product-card')) return 'product_card';
    if (element.closest('quick-add-modal, #QuickAddInfo, .quick-add-modal')) return 'quick_view';
    if (document.body.classList.contains('page-cart')) return 'cart';
    return 'main';
  }

  function getProductContext(element) {
    const productRoot = element.closest('[data-product-id], product-form, .card-wrapper, .card, .product');
    const productId = productRoot && productRoot.getAttribute('data-product-id');
    const form = element.closest('form') || (productRoot && productRoot.querySelector('form'));
    const variantInput = form && form.querySelector('[name="id"]');
    const variantId = variantInput && variantInput.value;
    const productInfo = productRoot && productRoot.closest('product-info');

    return {
      ...(productId ? { product_id: productId } : {}),
      ...(variantId ? { variant_id: variantId } : {}),
      ...(productInfo?.dataset.productHandle ? { product_handle: productInfo.dataset.productHandle } : {}),
      ...(productInfo?.dataset.templateSuffix ? { template_suffix: productInfo.dataset.templateSuffix } : {}),
      ...(productInfo?.dataset.currency ? { currency: productInfo.dataset.currency } : {}),
    };
  }

  function getMainProductRoot() {
    return document.querySelector('product-info[data-product-id]');
  }

  function getMainProductContext() {
    const root = getMainProductRoot();
    if (!root) return {};
    const form = root.querySelector('form[action*="/cart/add"], product-form form, form');
    const variantInput = form?.querySelector('[name="id"]');
    const quantityInput = form?.querySelector('.quantity__input');
    const price = Number(root.dataset.price);
    return {
      ...getProductContext(root),
      ...(quantityInput?.value ? { quantity: Number(quantityInput.value) || 1 } : {}),
      ...(Number.isFinite(price) ? { value: price } : {}),
      page_area: 'main',
    };
  }

  function errorCode(value) {
    const text = String(value || '').toLowerCase();
    if (text.includes('sold out') || text.includes('unavailable')) return 'sold_out';
    if (text.includes('variant')) return 'invalid_variant';
    if (text.includes('network') || text.includes('fetch')) return 'network_error';
    return 'cart_api_error';
  }

  function classifyFunnelClick(event) {
    const element = closestFromEvent(
      event,
      'a, button, input[type="submit"], [role="button"], shopify-accelerated-checkout'
    );
    if (!element) return null;
    const context = getProductContext(element);
    const paymentButton = element.closest('.shopify-payment-button, [data-shopify="payment-button"]');
    if (paymentButton) {
      return {
        placement: paymentButton.classList.contains('pdp-v2-payment-in-sticky')
          ? 'sticky_shop_pay'
          : 'dynamic_checkout',
        ...context,
      };
    }
    if (element.closest('#CartDrawer-Checkout')) {
      return { placement: 'cart_drawer', page_area: 'cart_drawer', ...context };
    }
    if (element.closest('#checkout') || element.closest('form[action*="/checkout"]')) {
      return { placement: 'cart', page_area: 'cart', ...context };
    }
    return null;
  }

  function publishProductView() {
    const root = getMainProductRoot();
    if (!root || seenProductViews.has(root.dataset.productId)) return;
    seenProductViews.add(root.dataset.productId);
    publishFunnel('product_viewed', getMainProductContext());
  }

  function subscribeToProductEvents() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const variant = event?.data?.variant;
      const variantId = variant?.id ? String(variant.id) : getMainProductContext().variant_id;
      if (!variantId || variantId === lastVariantId) return;
      lastVariantId = variantId;
      publishFunnel('variant_selected', {
        ...getMainProductContext(),
        variant_id: variantId,
        ...(variant?.price ? { value: Number(variant.price) / 100 } : {}),
      });
    });

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source !== 'product-form' || !pendingAddToCart) return;
      publishFunnel('add_to_cart_succeeded', {
        ...getMainProductContext(),
        variant_id: String(event.productVariantId || pendingAddToCart.variantId || ''),
        quantity: pendingAddToCart.quantity,
        placement: pendingAddToCart.placement || 'product_detail',
      });
      pendingAddToCart = null;
    });

    subscribe(PUB_SUB_EVENTS.cartError, (event) => {
      if (event?.source !== 'product-form' || !pendingAddToCart) return;
      publishFunnel('add_to_cart_failed', {
        ...getMainProductContext(),
        variant_id: String(event.productVariantId || pendingAddToCart.variantId || ''),
        quantity: pendingAddToCart.quantity,
        placement: pendingAddToCart.placement || 'product_detail',
        error_code: errorCode(event.errors || event.message),
      });
      pendingAddToCart = null;
    });
  }

  function classifyClick(event) {
    const element = closestFromEvent(
      event,
      'a, button, summary, input, select, label, [role="button"], shopify-accelerated-checkout, cart-remove-button'
    );
    if (!element) return null;

    const area = pageArea(element);
    const href = element.closest('a[href]')?.href || '';
    const context = getProductContext(element);

    if (element.matches('[data-pdp-sticky-option]')) {
      return {
        anchor_id: 'product.sticky.variant_select',
        anchor_category: 'product',
        page_area: 'main',
        placement: 'sticky_purchase_bar',
        ...getMainProductContext(),
      };
    }
    if (element.closest('[data-pdp-sticky-quantity]')) {
      const direction = element.closest('[data-pdp-sticky-quantity]').dataset.pdpStickyQuantity;
      return {
        anchor_id: `product.sticky.quantity_${direction}`,
        anchor_category: 'product',
        page_area: 'main',
        placement: 'sticky_purchase_bar',
        ...getMainProductContext(),
      };
    }
    if (element.closest('[data-pdp-sticky-submit]')) {
      return {
        anchor_id: 'product.sticky.buy_now',
        anchor_category: 'checkout',
        page_area: 'main',
        placement: 'sticky_buy_now',
        ...getMainProductContext(),
      };
    }
    if (element.closest('[data-pdp-sticky-add]')) {
      return {
        anchor_id: 'product.sticky.add_to_cart',
        anchor_category: 'product',
        page_area: 'main',
        placement: 'sticky_add_to_cart',
        ...getMainProductContext(),
      };
    }
    if (element.closest('[data-pdp-main-buy-now]')) {
      return {
        anchor_id: 'product.main.buy_now',
        anchor_category: 'checkout',
        page_area: 'main',
        placement: 'main_buy_now',
        ...getMainProductContext(),
      };
    }

    if (element.closest('.header__heading-link')) {
      return { anchor_id: 'header.logo.open_home', anchor_category: 'navigation', page_area: 'header' };
    }
    if (element.closest('.header__icon--search')) {
      return { anchor_id: 'header.search.open', anchor_category: 'navigation', page_area: 'header' };
    }
    if (element.closest('#cart-icon-bubble')) {
      return { anchor_id: 'header.cart.open', anchor_category: 'cart', page_area: 'header', placement: 'cart_drawer' };
    }
    if (area === 'header' && href && /\/account(?:\/|$)/.test(new URL(href, location.origin).pathname)) {
      return { anchor_id: 'header.account.open', anchor_category: 'account', page_area: 'header' };
    }
    if (area === 'header' && element.matches('summary') && element.closest('details, header-drawer')) {
      return { anchor_id: 'navigation.menu.open', anchor_category: 'navigation', page_area: 'header' };
    }
    if (element.closest('[name="quickView"]')) {
      return { anchor_id: 'product.card.quick_view', anchor_category: 'product', page_area: 'product_card', placement: 'quick_view', ...context };
    }
    if (href && /\/products\//.test(new URL(href, location.origin).pathname) && element.closest('.card-wrapper, .card, product-card')) {
      return { anchor_id: 'product.card.open', anchor_category: 'product', page_area: 'product_card', ...context };
    }
    if (event.type === 'change' && element.closest('variant-selects, variant-radios, .product-form__input') && element.matches('input, select')) {
      return { anchor_id: 'product.main.variant_select', anchor_category: 'product', page_area: area, ...context };
    }
    if (element.matches('[name="minus"], [name="plus"]')) {
      const direction = element.getAttribute('name') === 'minus' ? 'quantity_minus' : 'quantity_plus';
      const inCart = area === 'cart' || area === 'cart_drawer';
      return {
        anchor_id: `${inCart ? 'cart.item' : 'product.main'}.${direction}`,
        anchor_category: inCart ? 'cart' : 'product',
        page_area: area,
        ...context,
      };
    }
    if (element.closest('[id^="ProductSubmitButton-"]')) {
      return { anchor_id: 'product.main.add_to_cart', anchor_category: 'product', page_area: area, placement: area === 'product_card' ? 'product_card' : 'product_detail', ...context };
    }
    const paymentButton = element.closest('.shopify-payment-button, [data-shopify="payment-button"]');
    if (paymentButton) {
      const inSticky = paymentButton.classList.contains('pdp-v2-payment-in-sticky');
      return {
        anchor_id: inSticky ? 'product.sticky.shop_pay' : 'product.main.shop_pay',
        anchor_category: 'checkout',
        page_area: area,
        placement: inSticky ? 'sticky_shop_pay' : 'product_detail',
        ...context,
      };
    }
    if (href && new URL(href, location.origin).hostname === 'templaterenderer.tapque.com') {
      return { anchor_id: 'product.customizer.open', anchor_category: 'product', page_area: area, placement: 'product_customizer', ...context };
    }
    if (element.closest('cart-remove-button')) {
      return { anchor_id: 'cart.item.remove', anchor_category: 'cart', page_area: area, ...context };
    }
    if (element.closest('#CartDrawer-Checkout')) {
      return { anchor_id: 'cart.drawer.checkout', anchor_category: 'checkout', page_area: 'cart_drawer' };
    }
    if (element.closest('#checkout')) {
      return { anchor_id: 'cart.checkout', anchor_category: 'checkout', page_area: 'cart' };
    }
    if (
      element.matches('button, input[type="submit"]') &&
      element.closest('form[action*="/account/login"], form#customer_login')
    ) {
      return { anchor_id: 'account.login.submit', anchor_category: 'account', page_area: 'main' };
    }
    if (element.closest('form.newsletter-form') && element.closest('button[type="submit"], button[name="commit"]')) {
      return { anchor_id: 'newsletter.submit', anchor_category: 'account', page_area: area, placement: area };
    }

    return null;
  }

  function publishDirectCheckout(event, placement) {
    const detail = event.detail || {};
    const context = {
      ...getMainProductContext(),
      ...(detail.productId ? { product_id: String(detail.productId) } : {}),
      ...(detail.variantId ? { variant_id: String(detail.variantId) } : {}),
      ...(detail.quantity ? { quantity: Number(detail.quantity) || 1 } : {}),
      page_area: 'main',
      placement,
    };

    if (detail.stage === 'cart_add_attempted') {
      publishFunnel('add_to_cart_attempted', context);
      return;
    }
    if (detail.stage === 'cart_add_succeeded') {
      publishFunnel('add_to_cart_succeeded', context);
      return;
    }
    if (detail.stage === 'cart_add_failed') {
      publishFunnel('add_to_cart_failed', {
        ...context,
        error_code: detail.errorCode || 'cart_api_error',
      });
      return;
    }
    if (detail.stage === 'started') {
      publishFunnel('checkout_started', context);
    }
  }

  document.addEventListener('pdp:sticky-checkout', (event) => {
    publishDirectCheckout(event, 'sticky_buy_now');
  });

  document.addEventListener('pdp:main-buy-now', (event) => {
    publishDirectCheckout(event, 'main_buy_now');
  });

  document.addEventListener('click', (event) => {
    const payload = classifyClick(event);
    if (payload) publish(payload);
    const funnelPayload = classifyFunnelClick(event);
    if (funnelPayload) publishFunnel('checkout_started', funnelPayload);
  }, true);

  document.addEventListener('change', (event) => {
    const payload = classifyClick(event);
    if (payload) publish(payload);
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target instanceof Element ? event.target.closest('form[action*="/cart/add"], product-form form') : null;
    if (!form || !getMainProductRoot()?.contains(form)) return;
    const context = getMainProductContext();
    const placement = form.dataset.pdpSubmitPlacement || 'product_detail';
    delete form.dataset.pdpSubmitPlacement;
    pendingAddToCart = {
      id: ++submitSequence,
      variantId: context.variant_id,
      quantity: context.quantity || 1,
      placement,
    };
    publishFunnel('add_to_cart_attempted', {
      ...context,
      placement,
    });
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      publishProductView();
      lastVariantId = getMainProductContext().variant_id || null;
      subscribeToProductEvents();
    }, { once: true });
  } else {
    publishProductView();
    lastVariantId = getMainProductContext().variant_id || null;
    subscribeToProductEvents();
  }
})();
